import "../../Components/Widgets/ContactCard/ContactCard.scss";

function getScrollAmount(scroller) {
    const card = scroller.querySelector("[data-contact-card]:not([style*='display: none'])");
    if (!card) {
        return scroller.clientWidth;
    }

    const styles = window.getComputedStyle(scroller);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return card.getBoundingClientRect().width + gap;
}

function getMaxScroll(scroller) {
    return Math.max(0, scroller.scrollWidth - scroller.clientWidth);
}

function updateScrollTrack(root) {
    const scroller = root.querySelector("[data-contact-card-scroller]");
    const scrollNav = root.querySelector("[data-contact-card-scroll-nav]");
    const track = root.querySelector("[data-contact-card-scroll-track]");
    const thumb = root.querySelector("[data-contact-card-scroll-thumb]");
    if (!scroller || !track || !thumb) {
        return;
    }

    const maxScroll = getMaxScroll(scroller);
    const canScroll = maxScroll > 2;

    if (scrollNav) {
        scrollNav.hidden = !canScroll;
    }

    if (!canScroll) {
        return;
    }

    const visibleRatio = scroller.clientWidth / scroller.scrollWidth;
    const progress = maxScroll === 0 ? 0 : scroller.scrollLeft / maxScroll;
    const thumbWidth = Math.max(visibleRatio * 100, 8);
    const travel = 100 - thumbWidth;

    thumb.style.width = `${thumbWidth}%`;
    thumb.style.left = `${progress * travel}%`;
    track.setAttribute("aria-valuenow", String(Math.round(progress * 100)));
}

function getVisibleCards(scroller) {
    return Array.from(scroller.querySelectorAll("[data-contact-card]")).filter(
        (card) => card.style.display !== "none"
    );
}

function getScrollState(scroller) {
    const maxScroll = getMaxScroll(scroller);
    const canScroll = maxScroll > 2;
    const cards = getVisibleCards(scroller);

    if (!canScroll || cards.length === 0) {
        return { canScroll, atStart: true, atEnd: true };
    }

    const scrollerRect = scroller.getBoundingClientRect();
    const lastRect = cards[cards.length - 1].getBoundingClientRect();
    const edgeTolerance = 8;
    const atStart = scroller.scrollLeft <= 2;

    return {
        canScroll,
        atStart,
        atEnd: atStart
            ? false
            : lastRect.right <= scrollerRect.right + edgeTolerance
                || scroller.scrollLeft >= maxScroll - 2,
    };
}

function updateArrows(root) {
    const scroller = root.querySelector("[data-contact-card-scroller]");
    const prev = root.querySelector("[data-contact-card-prev]");
    const next = root.querySelector("[data-contact-card-next]");
    if (!scroller || !prev || !next) {
        return;
    }

    const { canScroll, atStart, atEnd } = getScrollState(scroller);

    root.classList.toggle("is-scrollable", canScroll);
    root.classList.toggle("is-at-start", atStart);
    root.classList.toggle("is-at-end", atEnd);
    prev.setAttribute("aria-disabled", !canScroll || atStart ? "true" : "false");
    next.setAttribute("aria-disabled", !canScroll || atEnd ? "true" : "false");
    updateScrollTrack(root);
}

function scheduleArrowUpdate(root) {
    updateArrows(root);
    window.requestAnimationFrame(() => updateArrows(root));
    window.setTimeout(() => updateArrows(root), 50);
    window.setTimeout(() => updateArrows(root), 200);
}

function applySearch(root, query) {
    const cards = Array.from(root.querySelectorAll("[data-contact-card]"));
    const empty = root.querySelector("[data-contact-card-empty]");
    const normalized = (query || "").trim().toLowerCase();
    let matchCount = 0;

    cards.forEach((card) => {
        const name = (card.getAttribute("data-name") || "").toLowerCase();
        const title = (card.getAttribute("data-title") || "").toLowerCase();
        const isMatch = !normalized || name.includes(normalized) || title.includes(normalized);
        card.style.display = isMatch ? "" : "none";
        if (isMatch) {
            matchCount += 1;
        }
    });

    const hasResults = matchCount > 0;
    root.classList.toggle("is-empty", !hasResults);
    if (empty) {
        empty.hidden = hasResults;
    }

    const scroller = root.querySelector("[data-contact-card-scroller]");
    if (scroller) {
        scroller.scrollTo({ left: 0 });
    }

    updateArrows(root);
}

function scrollFromTrackPosition(root, clientX) {
    const scroller = root.querySelector("[data-contact-card-scroller]");
    const track = root.querySelector("[data-contact-card-scroll-track]");
    if (!scroller || !track) {
        return;
    }

    const maxScroll = getMaxScroll(scroller);
    if (maxScroll <= 0) {
        return;
    }

    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    scroller.scrollLeft = ratio * maxScroll;
}

function initDragScroll(scroller, root) {
    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    const endDrag = () => {
        if (!isDragging) {
            return;
        }

        isDragging = false;
        scroller.classList.remove("is-dragging");
        scheduleArrowUpdate(root);
    };

    scroller.addEventListener("mousedown", (event) => {
        if (event.button !== 0) {
            return;
        }

        const target = event.target;
        if (target.closest("a, button, summary, input, textarea, select, label")) {
            return;
        }

        isDragging = true;
        startX = event.pageX;
        startScrollLeft = scroller.scrollLeft;
        scroller.classList.add("is-dragging");
        event.preventDefault();
    });

    window.addEventListener("mousemove", (event) => {
        if (!isDragging) {
            return;
        }

        scroller.scrollLeft = startScrollLeft - (event.pageX - startX);
    });

    window.addEventListener("mouseup", endDrag);
    scroller.addEventListener("mouseleave", endDrag);
}

function initScrollTrack(root) {
    const track = root.querySelector("[data-contact-card-scroll-track]");
    if (!track) {
        return;
    }

    let isDragging = false;

    const endDrag = () => {
        isDragging = false;
        track.classList.remove("is-dragging");
        scheduleArrowUpdate(root);
    };

    track.addEventListener("mousedown", (event) => {
        if (event.button !== 0) {
            return;
        }

        isDragging = true;
        track.classList.add("is-dragging");
        scrollFromTrackPosition(root, event.clientX);
        event.preventDefault();
    });

    window.addEventListener("mousemove", (event) => {
        if (!isDragging) {
            return;
        }

        scrollFromTrackPosition(root, event.clientX);
    });

    window.addEventListener("mouseup", endDrag);

    track.addEventListener("keydown", (event) => {
        const scroller = root.querySelector("[data-contact-card-scroller]");
        if (!scroller) {
            return;
        }

        const amount = getScrollAmount(scroller);
        if (event.key === "ArrowRight") {
            scroller.scrollBy({ left: amount, behavior: "smooth" });
            event.preventDefault();
        } else if (event.key === "ArrowLeft") {
            scroller.scrollBy({ left: -amount, behavior: "smooth" });
            event.preventDefault();
        }
    });
}

function initContactCards(root) {
    const scroller = root.querySelector("[data-contact-card-scroller]");
    const prev = root.querySelector("[data-contact-card-prev]");
    const next = root.querySelector("[data-contact-card-next]");
    const searchForm = root.querySelector("[data-contact-card-search]");
    const searchInput = searchForm ? searchForm.querySelector("input[type='search']") : null;

    if (searchForm) {
        searchForm.addEventListener("submit", (event) => {
            event.preventDefault();
            applySearch(root, searchInput ? searchInput.value : "");
        });
    }

    if (searchInput) {
        searchInput.addEventListener("input", () => applySearch(root, searchInput.value));
    }

    if (prev && scroller) {
        prev.addEventListener("click", () => {
            if (prev.getAttribute("aria-disabled") === "true") {
                return;
            }

            scroller.scrollBy({ left: -getScrollAmount(scroller), behavior: "smooth" });
            scheduleArrowUpdate(root);
        });
    }

    if (next && scroller) {
        next.addEventListener("click", () => {
            if (next.getAttribute("aria-disabled") === "true") {
                return;
            }

            scroller.scrollBy({ left: getScrollAmount(scroller), behavior: "smooth" });
            scheduleArrowUpdate(root);
        });
    }

    if (scroller) {
        if (scroller.scrollLeft <= 2) {
            scroller.scrollLeft = 0;
        }

        scroller.addEventListener("scroll", () => scheduleArrowUpdate(root), { passive: true });
        scroller.addEventListener("scrollend", () => scheduleArrowUpdate(root), { passive: true });
        initDragScroll(scroller, root);

        const images = scroller.querySelectorAll("img");
        images.forEach((image) => {
            if (!image.complete) {
                image.addEventListener("load", () => scheduleArrowUpdate(root), { once: true });
            }
        });
    }

    initScrollTrack(root);

    window.addEventListener("resize", () => scheduleArrowUpdate(root));
    scheduleArrowUpdate(root);
}

export function initAllContactCards() {
    document.querySelectorAll("[data-contact-cards]").forEach((root) => {
        if (root.dataset.contactCardsReady === "true") {
            return;
        }

        root.dataset.contactCardsReady = "true";
        initContactCards(root);
    });
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllContactCards);
} else {
    initAllContactCards();
}
